import React, { useState } from 'react';
import { FileText, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { InlineMicButton } from '../InlineMicButton';
import { DeleteConfirmModal } from '../DeleteConfirmModal';
import { useApi } from '../../hooks/useApi';
import type { Surgery } from '../../types/clinical';

interface Report {
  id: string;
  templateType: string;
  reportText: string;
  pdfGcsPath?: string;
  createdAt: string;
}

export interface ReportSectionProps {
  reports: Report[];
  surgeries: Surgery[];
  isDoctor: boolean;
  patientId: string;
  // Form state
  showReportForm: boolean;
  setShowReportForm: (v: boolean) => void;
  reportType: string;
  setReportType: (v: string) => void;
  reportFindings: string;
  setReportFindings: React.Dispatch<React.SetStateAction<string>>;
  reportDraft: string;
  setReportDraft: (v: string) => void;
  reportDrafting: boolean;
  reportSaving: boolean;
  // Handlers
  onDraftReport: () => void;
  onSaveReport: () => void;
  onReportsChanged: (reports: Report[]) => void;
}

export function ReportSection({
  reports, surgeries, isDoctor, patientId,
  showReportForm, setShowReportForm,
  reportType, setReportType,
  reportFindings, setReportFindings,
  reportDraft, setReportDraft,
  reportDrafting, reportSaving,
  onDraftReport, onSaveReport, onReportsChanged,
}: ReportSectionProps) {
  const { get, post, del: apiDel } = useApi();
  const [selectedSurgeryId, setSelectedSurgeryId] = useState('');
  const [deleteReportId, setDeleteReportId] = useState<string | null>(null);
  const [surgicalReportLoading, setSurgicalReportLoading] = useState(false);

  const isSurgicalReport = reportType === 'surgical_report';

  const handleGenerateSurgicalReport = async () => {
    if (!selectedSurgeryId || !patientId) return;
    setSurgicalReportLoading(true);
    try {
      const res = await post<{ pdfUrl: string; reportId: string }>(`/clinical/${patientId}/surgeries/${selectedSurgeryId}/report`, {});
      if (res.success && res.data?.pdfUrl) {
        window.open(res.data.pdfUrl, '_blank', 'noopener,noreferrer');
        // Refresh reports list
        const listRes = await get<Report[]>(`/clinical/${patientId}/reports`);
        if (listRes.success && listRes.data) onReportsChanged(listRes.data);
        setShowReportForm(false);
        setSelectedSurgeryId('');
      }
    } catch { /* error handled by useApi */ }
    finally { setSurgicalReportLoading(false); }
  };

  const handleDeleteReport = async () => {
    if (!deleteReportId || !patientId) return;
    await apiDel(`/clinical/${patientId}/reports/${deleteReportId}`);
    onReportsChanged(reports.filter(r => r.id !== deleteReportId));
    setDeleteReportId(null);
  };

  return (
    <>
    {deleteReportId && (
      <DeleteConfirmModal
        title="Delete Report"
        description="This will permanently delete this report. This action cannot be undone."
        onConfirm={handleDeleteReport}
        onCancel={() => setDeleteReportId(null)}
      />
    )}
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
          <FileText className="h-4 w-4" /> Reports ({reports.length})
        </h2>
        {isDoctor && (
          <button onClick={() => setShowReportForm(!showReportForm)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Generate Report
          </button>
        )}
      </div>

      {showReportForm && (
        <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Report Type</label>
              <select value={reportType} onChange={e => { setReportType(e.target.value); setSelectedSurgeryId(''); }} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
                <option value="referral_letter">Referral Letter</option>
                <option value="antenatal_summary">Antenatal Summary</option>
                <option value="post_operative_report">Post-Operative Report</option>
                <option value="surgical_report">Surgical Report (verbatim)</option>
                <option value="sick_certificate">Sick Certificate</option>
                <option value="motivation_letter">Motivation Letter (Pre-Auth)</option>
                <option value="discharge_summary">Discharge Summary</option>
                <option value="pathology_letter">Pathology Results Letter</option>
              </select>
            </div>
          </div>

          {isSurgicalReport ? (
            /* Surgical report — select surgery, generate verbatim PDF (no AI) */
            <>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Select Surgery</label>
                {surgeries.length === 0 ? (
                  <p className="text-sm text-gray-500">No surgeries recorded for this patient.</p>
                ) : (
                  <select
                    value={selectedSurgeryId}
                    onChange={e => setSelectedSurgeryId(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
                  >
                    <option value="">— Select a surgery —</option>
                    {surgeries.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.procedure} — {s.date}{s.hospital ? ` (${s.hospital})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-3">
                This report reproduces the surgery record exactly as entered. No AI drafting — your exact notes will appear on the letterhead.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleGenerateSurgicalReport}
                  disabled={!selectedSurgeryId || surgicalReportLoading}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                >
                  {surgicalReportLoading ? <><Loader2 className="h-3 w-3 animate-spin" /> Generating...</> : <><Save className="h-3.5 w-3.5" /> Generate Surgical Report</>}
                </button>
                <button onClick={() => setShowReportForm(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              </div>
            </>
          ) : (
            /* AI-drafted reports — existing flow */
            <>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-500">Clinical Findings / Context</label>
                  <InlineMicButton onTranscript={t => setReportFindings(prev => prev ? prev + ' ' + t : t)} />
                </div>
                <textarea value={reportFindings} onChange={e => setReportFindings(e.target.value)} rows={4} placeholder="Describe the clinical findings, reason for referral, relevant history..." className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
              </div>
              {!reportDraft ? (
                <div className="flex gap-2">
                  <button onClick={onDraftReport} disabled={reportDrafting || !reportFindings.trim()} className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                    {reportDrafting ? <><Loader2 className="h-3 w-3 animate-spin" /> Drafting...</> : 'Generate Draft with AI'}
                  </button>
                  <button onClick={() => setShowReportForm(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Report Text (review and edit before saving)</label>
                    <textarea value={reportDraft} onChange={e => setReportDraft(e.target.value)} rows={10} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={onSaveReport} disabled={reportSaving} className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1">
                      {reportSaving ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving...</> : <><Save className="h-3.5 w-3.5" /> Save & Generate PDF</>}
                    </button>
                    <button onClick={() => setReportDraft('')} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Re-draft</button>
                    <button onClick={() => { setShowReportForm(false); setReportDraft(''); setReportFindings(''); }} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {reports.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">No reports generated.</p>
      ) : (
        <div className="space-y-2">
          {reports.map(rpt => (
            <div key={rpt.id} className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-900">{rpt.templateType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  <span className="text-xs text-gray-500 ml-2">{new Date(rpt.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                <div className="flex items-center gap-2">
                  {rpt.pdfGcsPath && (
                    <button
                      onClick={async () => {
                        const res = await get<{ pdfUrl: string }>(`/clinical/${patientId}/reports/${rpt.id}/download`);
                        if (res.success && res.data?.pdfUrl) window.open(res.data.pdfUrl, '_blank', 'noopener,noreferrer');
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      PDF
                    </button>
                  )}
                  {isDoctor && (
                    <button onClick={() => setDeleteReportId(rpt.id)} className="p-1 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              </div>
              {rpt.reportText && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{rpt.reportText}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
}
